import { PersonAvatar, ElementsButton, SideOverHeading, IconButton } from '../components';
import {
  PersonForm,
  PersonDateOfBirthDatePicker,
  PersonEmailInput,
  PersonFamilyNameInput,
  PersonGivenNameInput,
  PersonOccupationInput,
  PersonPhoneNumberInput,
  PersonRelationshipDropdown,
  PersonDeleteModal,
} from '../../Person';
import { SideOverFooter, SideOverHeaderNew, useSideOverContext } from '../../../src/UI/components/SideOver';
import { Person, RelationshipType } from '../../generated/graphql';
import { fadeSlideIn } from '../animation';
import { motion } from 'framer-motion';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PersonFormProps {
  person?: Person;
  hideDelete?: boolean;
  onUpdate?: () => void;
  onCreate?: () => void;
  householdID?: string | null;
  currentUserID: string | undefined;
}

export const PersonDetailsSideOverPage: FC<PersonFormProps> = ({
  person,
  onUpdate,
  onCreate,
  hideDelete,
  householdID,
  currentUserID,
}) => {
  const { t: tForm } = useTranslation('form');
  const { closeSideOver } = useSideOverContext();
  const { t: tCommon } = useTranslation('common');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allowDelete, setAllowDelete] = useState(false);

  useEffect(() => {
    setAllowDelete(person?.relationship !== RelationshipType.HeadOfHousehold && currentUserID !== person?.userID);
  }, [person, currentUserID]);

  return (
    <>
      <SideOverHeaderNew
        withShadow
        className="md:!px-4"
        exitIcon={person ? 'back' : 'close'}
        exitText={person ? tCommon('back') : tCommon('cancel')}
      >
        {!hideDelete && allowDelete && person && (
          <>
            <ElementsButton
              theme="danger"
              iconPosition="left"
              icon={'removeCircle'}
              label={tCommon('remove')}
              className="ml-auto hidden sm:flex"
              data-testid="remove-person-button"
              onClick={() => setIsModalOpen(true)}
            />
            <IconButton
              theme="danger"
              icon="removeCircle"
              className="sm:hidden ml-auto"
              data-testid="remove-person-button"
              onClick={() => setIsModalOpen(true)}
            />
          </>
        )}
      </SideOverHeaderNew>
      <motion.div className="md:px-10 px-4 pb-32" {...fadeSlideIn()}>
        <SideOverHeading heading={person?.id ? tForm('edit-individual') : tForm('add-individual')} />
        <PersonAvatar className="mb-10" person={person} />
        <PersonForm
          person={person}
          createPersonMutationOptions={{
            onSuccess: () => {
              onCreate?.();
              closeSideOver();
            },
          }}
          updatePersonMutationOptions={{
            onSuccess: () => {
              onUpdate?.();
              closeSideOver();
            },
          }}
          householdID={householdID}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2 md:col-span-1">
              <PersonRelationshipDropdown />
            </div>
            <PersonGivenNameInput className="col-span-2 md:col-span-1 md:row-start-2" />
            <PersonFamilyNameInput className="col-span-2 md:col-span-1 md:row-start-2" />
            <div className="col-span-2 md:col-span-1 md:row-start-3">
              <PersonDateOfBirthDatePicker />
            </div>
            <PersonEmailInput className="col-span-2" />
            <PersonPhoneNumberInput className="col-span-2 md:col-span-1 md:row-start-5" />
            <PersonOccupationInput className="col-span-2 md:col-span-1 md:row-start-5" />
          </div>
          <SideOverFooter>
            <ElementsButton type="submit" label={tCommon('save')} icon="check" iconPosition="left" />
          </SideOverFooter>
        </PersonForm>
      </motion.div>
      {!hideDelete && allowDelete && person && (
        <PersonDeleteModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} person={person} />
      )}
    </>
  );
};
